import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Checkbox, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import styled, { css } from 'styled-components';
import {
  associatedSpaceTypesAndWorksheets,
  selectAssociatedFloorplans,
} from 'spw-redux/modules/project-space-type-categories/selectors';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import { PROGRAMMING } from 'spw-constants';
import { shallowEqual, useSelector } from 'react-redux';
import { Italic } from '../styles';

interface CustomSpaceCategoryItemProps {
  id: number;
  markedForDelete?: boolean;
  loading?: boolean;
  confirmationValue?: boolean;
  onConfirmationChange?: (value: boolean) => void;
  onRemoveFromDelete: (id: number) => void;
  children: ReactNode | ReactNode[];
}

const ContentWrapper = styled.div`
  position: relative;
`;

const NormalContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 16px;
`;

const ConfirmationContainer = styled.div`
  border: 1px solid #f01346;
  border-radius: 4px;
  padding: 14px 16px;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 7.75px;
`;

const ConfirmationTitle = styled.h2`
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #f01346;
  margin-top: 0;
  margin-bottom: 0;
`;

const TextStyle = css`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

const Text = styled.span`
  ${TextStyle}
  color: #000000;
`;

const ItalicText = styled.span`
  font-family: 'Roboto';
  font-style: italic;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #707070;
`;

const CheckboxContainer = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
`;

const ConfirmationText = styled.span`
  ${TextStyle}
  color: #F01346;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CustomSpaceCategoryItem: React.FC<CustomSpaceCategoryItemProps> = ({
  id,
  children,
  markedForDelete,
  loading,
  confirmationValue,
  onConfirmationChange,
  onRemoveFromDelete,
}) => {
  const alreadyConfirmed = useRef(false);
  const project = useSelector(currentProject);
  const { associatedSpaceTypes, associatedWorksheets } = useSelector(
    associatedSpaceTypesAndWorksheets,
    shallowEqual
  );
  const associatedFloorplans = useSelector(selectAssociatedFloorplans);

  const needsConfirmation = useMemo(() => {
    if (project.type === PROGRAMMING) {
      return associatedWorksheets > 0 || associatedSpaceTypes > 0;
    }
    return associatedFloorplans > 0 || associatedSpaceTypes > 0;
  }, [
    project.type,
    associatedSpaceTypes,
    associatedWorksheets,
    associatedFloorplans,
  ]);

  useEffect(() => {
    if (
      markedForDelete &&
      !loading &&
      !needsConfirmation &&
      !alreadyConfirmed.current
    ) {
      onConfirmationChange(true);
      alreadyConfirmed.current = true;
    } else {
      alreadyConfirmed.current = false;
    }
  }, [markedForDelete, loading, needsConfirmation, onConfirmationChange]);

  return (
    <ContentWrapper>
      {onRemoveFromDelete && markedForDelete && (
        <CloseButtonContainer>
          <IconButton
            aria-label="reset title"
            onClick={() => onRemoveFromDelete(id)}
            edge="end"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </CloseButtonContainer>
      )}
      {loading ? (
        <NormalContainer>
          <StatusContainer>
            <CircularProgress
              color="secondary"
              size={16}
              style={{ marginRight: '10.5px' }}
            />
            <ItalicText>Checking for associated data...</ItalicText>
          </StatusContainer>
        </NormalContainer>
      ) : (
        <>
          {markedForDelete ? (
            <>
              {needsConfirmation ? (
                <ConfirmationContainer>
                  <ConfirmationTitle>CAUTION</ConfirmationTitle>
                  <ConfirmationText>
                    <strong>{associatedSpaceTypes}</strong> space types are
                    associated with this subcategory and{' '}
                    {project.type === PROGRAMMING ? (
                      <>
                        <strong>{associatedWorksheets}</strong>{' '}
                        {associatedWorksheets === 1
                          ? 'worksheet is'
                          : 'worksheets are'}
                      </>
                    ) : (
                      <>
                        <strong>{associatedFloorplans} </strong>{' '}
                        {associatedFloorplans === 1
                          ? 'floor plan is'
                          : 'floor plans are'}
                      </>
                    )}{' '}
                    using these space types.
                  </ConfirmationText>
                  <CheckboxContainer>
                    <Checkbox
                      checked={confirmationValue}
                      onChange={(e) =>
                        onConfirmationChange
                          ? onConfirmationChange(e.target.checked)
                          : null
                      }
                    />
                    <Text>
                      I understand that by clicking SAVE, the subcategory and
                      all associated space types will be permanently deleted.
                    </Text>
                  </CheckboxContainer>
                </ConfirmationContainer>
              ) : (
                <NormalContainer>
                  <StatusContainer style={{ marginBottom: 16 }}>
                    <CheckIcon
                      htmlColor="#03B2AB"
                      style={{ marginRight: '10.5px' }}
                    />
                    <Italic>
                      No associated data found. The subcategory is safe to
                      delete.
                    </Italic>
                  </StatusContainer>
                  <Text>
                    Clicking SAVE will permanently delete the subcategory.
                  </Text>
                </NormalContainer>
              )}
            </>
          ) : (
            <>{children}</>
          )}
        </>
      )}
    </ContentWrapper>
  );
};

export default CustomSpaceCategoryItem;
